/**
 * Reference and analysis (plan §3 step 1, §3.1 re-analysing): photo upload, the intake run
 * over SSE, the replace-or-new choice, refusals and the manual path.
 */
import { reactive } from 'vue';
import type { ProposalWithFields } from '@/domain';
import { cloneDesign, createDesign, fieldsForManualSetup } from '@/domain';
import type { FitCandidate, FitResult } from '@/families/fit-schema';
import { FAMILIES, getFamily } from '@/families/registry';
import { api, ApiError, subscribeEvents } from './api';
import { announce, toast } from './announce';
import { mapProposal, refusalFrom } from './intake';
import { plural } from './format';
import { app, snap, ui, type RefImage } from './state';
import { go } from './navigation';
import { flushSave, newProject, scheduleSave } from './persistence';

export const ANALYSIS_STAGES = [
  { id: 'uploading', label: 'Uploading images' },
  { id: 'reading-images', label: 'Reading the photos' },
  { id: 'reading-text', label: 'Reading the product text' },
  { id: 'proposing', label: 'Proposing a layout' },
] as const;

export const projectHasValues = (): boolean => !ui.empty && snap.value.revisions.length > 0;

/** The intake error code for "fixture mode, and no recording answers these photos". */
export const ANALYSIS_UNAVAILABLE = 'analysis-unavailable';
/** Demo mode: the photo is not one of the prepared ones (server/intake/demo.ts). */
export const DEMO_UNRECORDED = 'demo-unrecorded';

/**
 * Where the proposal in front of the user came from, when it was a recorded fixture rather than
 * a reading of their photo. Kept per project for this session (the project file does not record it).
 */
const fixtureSource = reactive({ projectId: null as string | null, fixture: null as string | null, match: null as string | null });

/** The fixture that produced the current project's proposal, or null for a live (or no) analysis. */
export function proposalFixture(): string | null {
  return fixtureSource.projectId !== null && fixtureSource.projectId === ui.projectId ? fixtureSource.fixture : null;
}
/** How that fixture was chosen: `photo` means it was recorded from this exact photo. */
export function proposalFixtureMatch(): string | null {
  return proposalFixture() ? fixtureSource.match : null;
}

function noteProposalSource(data: { mode?: string; fixture?: string; fixtureMatch?: string }): void {
  fixtureSource.projectId = ui.projectId;
  fixtureSource.fixture = data.mode === 'fixture' ? data.fixture ?? 'fixture' : null;
  fixtureSource.match = data.mode === 'fixture' ? data.fixtureMatch ?? null : null;
}

let closeStream: (() => void) | null = null;
let runId: string | null = null;

// ---------------------------------------------------------------------------------------------
// Choice dialog
// ---------------------------------------------------------------------------------------------
export function choose(opts: { title: string; lede: string; choices: { label: string; sub?: string; primary?: boolean; act: () => void }[] }): void {
  ui.choice = opts;
}

// ---------------------------------------------------------------------------------------------
// Photos
// ---------------------------------------------------------------------------------------------
export const RIGHTS_BASES = [
  'My own photo',
  'From the product page, for personal reference',
  'Shared with permission',
] as const;

async function uploadTo(projectId: string, file: Blob, name: string, rightsBasis: string): Promise<RefImage> {
  const up = await api.uploadImage(projectId, file, name);
  return {
    id: up.imageId, src: api.imageUrl(projectId, up.imageId), thumb: api.thumbUrl(projectId, up.imageId), path: up.path,
    alt: `Reference photo: ${name.replace(/\.[a-z0-9]+$/i, '')}`, sha256: up.sha256, rightsBasis, width: up.width, height: up.height,
  };
}

/** The photo upload in flight, if any: Analyse waits for it rather than racing it. */
let pendingUpload: Promise<void> | null = null;

/** Upload chosen files; asks replace-or-new first when the project already has checked values. */
export async function addPhotos(files: File[], rightsBasis: string, { replace = true }: { replace?: boolean } = {}): Promise<void> {
  if (!files.length || !ui.projectId) return;
  const apply = (newProjectFirst: boolean): Promise<void> => {
    const run = upload(newProjectFirst).finally(() => { if (pendingUpload === run) pendingUpload = null; });
    pendingUpload = run;
    return run;
  };
  const upload = async (newProjectFirst: boolean) => {
    try {
      if (newProjectFirst) {
        const keep = { url: ui.ref.url, text: ui.ref.text };
        await newProject({ from: 'blank' });
        ui.ref.url = keep.url;
        ui.ref.text = keep.text;
      }
      const uploaded: RefImage[] = [];
      for (const f of files) uploaded.push(await uploadTo(ui.projectId!, f, f.name, rightsBasis));
      ui.ref.images = replace || newProjectFirst ? uploaded : [...ui.ref.images, ...uploaded];
      if (ui.analysis.status === 'refused' || ui.analysis.status === 'error') ui.analysis = projectHasValues() ? { ...ui.analysis, status: 'done' } : { status: 'idle' };
      toast(`${plural(uploaded.length, 'photo')} added. Run the analysis to see what it makes of ${uploaded.length === 1 ? 'it' : 'them'}.`);
      announce(`${plural(uploaded.length, 'photo')} added.`);
      scheduleSave();
      if (newProjectFirst) go('reference');
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'The photos could not be uploaded.', { tone: 'warn' });
    }
  };
  if (!projectHasValues()) return apply(false);
  choose({
    title: 'Change the photos of this project?',
    lede: 'This project has checked values. New photos change nothing until you analyse them, and analysing will ask before it replaces anything.',
    choices: [
      { label: 'Start a new project from these photos', sub: 'This project stays saved', act: () => { void apply(true); } },
      { label: 'Use them in this project', sub: 'Values stay until you analyse again', primary: true, act: () => { void apply(false); } },
    ],
  });
}

export function removePhoto(id: string): void {
  ui.ref.images = ui.ref.images.filter((i) => i.id !== id);
  scheduleSave();
}

/** Copy the current photos into another project (for "Start a new project from these photos"). */
async function copyPhotosTo(projectId: string, images: RefImage[]): Promise<RefImage[]> {
  const out: RefImage[] = [];
  for (const im of images) {
    const blob = await (await fetch(im.src)).blob();
    out.push(await uploadTo(projectId, blob, `${im.id}.jpg`, im.rightsBasis));
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// Analysis
// ---------------------------------------------------------------------------------------------
export function requestAnalysis(): void {
  if (!projectHasValues()) { void runAnalysis({ mode: 'new' }); return; }
  choose({
    title: 'Analyse these photos?',
    lede: 'Your project already has checked values. Choose what the new proposal should do.',
    choices: [
      { label: 'Replace this project’s values', sub: 'Undo restores them', primary: true, act: () => { void runAnalysis({ mode: 'replace' }); } },
      { label: 'Start a new project from these photos', sub: 'This project stays saved', act: () => { void runAnalysis({ mode: 'new' }); } },
    ],
  });
}

export async function runAnalysis({ mode = 'replace' }: { mode?: 'replace' | 'new' } = {}): Promise<void> {
  if (ui.analysis.status === 'running') return;
  if (pendingUpload) await pendingUpload;
  if (!ui.ref.images.length) { toast('Add at least one photo first.', { tone: 'warn' }); return; }
  const prior = { ...ui.analysis };
  ui.analysis = { status: 'running', stage: 'uploading', stageIndex: 0, stageLabel: ANALYSIS_STAGES[0].label, ranAt: prior.ranAt ?? null, mode };
  try {
    if (mode === 'new' && projectHasValues()) {
      const keep = { url: ui.ref.url, text: ui.ref.text, images: ui.ref.images.slice() };
      await newProject({ from: 'blank' });
      ui.ref.url = keep.url;
      ui.ref.text = keep.text;
      ui.ref.images = await copyPhotosTo(ui.projectId!, keep.images);
      ui.analysis = { status: 'running', stage: 'uploading', stageIndex: 0, stageLabel: ANALYSIS_STAGES[0].label, ranAt: null, mode };
    }
    await flushSave();
    const started = await api.startIntake(ui.projectId!, { imageIds: ui.ref.images.map((i) => i.id), url: ui.ref.url, pastedText: ui.ref.text });
    runId = started.runId;
    ui.analysis.runId = started.runId;
  } catch (e) {
    ui.analysis = { status: 'error', error: e instanceof ApiError ? e.message : 'The analysis could not start.', ranAt: prior.ranAt ?? null, mode };
    return;
  }
  await new Promise<void>((resolve) => {
    let done = false;
    /** Time spent waiting to reconnect (the sum of the backoffs, so a sleeping laptop does not count). */
    let lostFor: number | null = null;
    let retryT: ReturnType<typeof setTimeout> | null = null;
    let idleT: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    const id = runId!;
    const end = () => {
      if (done) return;
      done = true;
      if (retryT) clearTimeout(retryT);
      if (idleT) clearTimeout(idleT);
      window.removeEventListener('online', wake);
      window.removeEventListener('pageshow', wake);
      document.removeEventListener('visibilitychange', onVisible);
      closeStream?.();
      closeStream = null;
      runId = null;
      resolve();
    };
    const handlers: Record<string, (d: unknown) => void> = {
      stage: (d) => {
        lostFor = null;
        attempt = 0;
        const x = d as { stage: string; label?: string };
        const i = ANALYSIS_STAGES.findIndex((s) => s.id === x.stage);
        if (i >= 0 && i < (ui.analysis.stageIndex ?? 0)) return; // a replay after reconnecting
        ui.analysis = { ...ui.analysis, stage: x.stage, stageIndex: Math.max(0, i), stageLabel: x.label ?? ANALYSIS_STAGES[Math.max(0, i)]?.label };
        announce(`${ui.analysis.stageLabel}…`);
      },
      proposal: (d) => { end(); onProposal(d as ProposalEvent, prior, mode); },
      refusal: (d) => {
        end();
        const r = d as { reason: string; detected?: { family: string; bays: number }; candidates?: FitCandidate[]; stale?: boolean; rawResponsePath?: string };
        ui.analysis = { status: 'refused', refusal: refusalFrom(r), ranAt: new Date().toISOString(), mode, rawResponsePath: r.rawResponsePath ?? null, proposal: prior.proposal ?? null, fit: prior.fit ?? null };
        announce('We can’t rebuild this one yet: it isn’t a shelf style we know. Nothing was measured or guessed.');
        scheduleSave();
      },
      error: (d) => {
        end();
        const x = d as { code?: string; message?: string };
        ui.analysis = { status: 'error', error: x.message || 'The analysis stopped.', ...(x.code ? { code: x.code } : {}), ranAt: prior.ranAt ?? null, mode, proposal: prior.proposal ?? null };
        announce('The analysis stopped.');
      },
      cancelled: () => { end(); ui.analysis = prior; announce('Analysis cancelled.'); },
      /** A long analysis says so instead of looking stuck ({ code: 'still-analysing', message, elapsedMs, timeoutMs }). */
      notice: (d) => {
        lostFor = null;
        const x = d as { code?: string; message?: string };
        if (ui.analysis.status !== 'running' || !x.message) return;
        if (ui.analysis.notice !== x.message) announce(x.message);
        ui.analysis = { ...ui.analysis, notice: x.message };
      },
    };
    /**
     * A stream that goes quiet without an error (the network suspended under a sleeping laptop,
     * ERR_NETWORK_IO_SUSPENDED) is re-opened after STREAM_IDLE_MS without an event: the service
     * replays the run, so the result is applied if it finished meanwhile. Coming back online or to
     * the tab re-opens it at once.
     */
    const kick = () => {
      if (idleT) clearTimeout(idleT);
      if (!done) idleT = setTimeout(reopen, STREAM_IDLE_MS);
    };
    const reopen = () => {
      if (done) return;
      if (retryT) { clearTimeout(retryT); retryT = null; }
      closeStream?.();
      closeStream = null;
      connect();
    };
    const wake = () => reopen();
    const onVisible = () => { if (document.visibilityState === 'visible') reopen(); };
    window.addEventListener('online', wake);
    window.addEventListener('pageshow', wake);
    document.addEventListener('visibilitychange', onVisible);
    const live: Record<string, (d: unknown) => void> = Object.fromEntries(Object.entries(handlers).map(([k, fn]) => [k, (d: unknown) => { kick(); fn(d); }]));
    const giveUp = (msg: string) => {
      end();
      if (ui.analysis.status === 'running') ui.analysis = { status: 'error', error: msg, code: 'connection-lost', ranAt: prior.ranAt ?? null, mode, proposal: prior.proposal ?? null };
      announce('The analysis stopped.');
    };
    /**
     * HD-11: the events stream dropped before its terminal event (an aborted request, a proxy
     * error, a sleeping laptop). The service keeps the run and replays every event to a new
     * subscriber, so reconnect with a backoff and apply whatever the run finished with; only
     * after STREAM_RECOVERY_MS without the stream coming back, or when the run is gone, say so.
     */
    const recover = () => {
      if (done) return;
      closeStream?.();
      closeStream = null;
      if (idleT) { clearTimeout(idleT); idleT = null; }
      lostFor ??= 0;
      if (lostFor > STREAM_RECOVERY_MS) {
        giveUp('The connection to the local service was lost during the analysis and did not come back. Your project is unchanged; try again.');
        return;
      }
      const wait = Math.min(4000, 500 * 2 ** attempt++);
      lostFor += wait;
      if (ui.analysis.status === 'running') ui.analysis = { ...ui.analysis, stageLabel: 'Reconnecting to the analysis' };
      retryT = setTimeout(() => {
        retryT = null;
        if (done) return;
        void api.intakeRunExists(id).then((exists) => {
          if (done) return;
          if (exists === false) { giveUp('The analysis could not be found on the local service (it may have restarted). Your project is unchanged; try again.'); return; }
          connect();
        });
      }, wait);
    };
    const connect = () => {
      closeStream = subscribeEvents(api.intakeEventsUrl(id), live, recover);
      kick();
    };
    connect();
  });
}

/** How long the app keeps reconnecting to a dropped analysis stream before it gives up (HD-11). */
export const STREAM_RECOVERY_MS = 90_000;
/** A stream with no event for this long is re-opened (the service replays the run). */
export const STREAM_IDLE_MS = 20_000;

interface ProposalEvent {
  fit: FitResult;
  familyId: string;
  confidence: number;
  flagged: boolean;
  runnersUp: FitCandidate[];
  fields: ProposalWithFields;
  createdAt?: string;
  stale?: boolean;
  rawResponsePath?: string;
  usage?: Record<string, number> | null;
  mode?: string;
  fixture?: string;
  fixtureMatch?: string;
}

function onProposal(data: ProposalEvent, prior: typeof ui.analysis, mode: 'replace' | 'new'): void {
  if (data.stale) {
    ui.analysis = prior;
    toast('The analysis finished after you changed the project, so its proposal was set aside. Your values are unchanged.', { tone: 'warn' });
    announce('Proposal discarded: the project changed while it ran.');
    return;
  }
  noteProposalSource(data);
  const createdAt = data.createdAt ?? new Date().toISOString();
  const mapped = mapProposal(data.fields, { createdAt, fallback: createDesign('even', app.store.stock), stock: app.store.stock });
  ui.analysis = {
    status: 'review', ranAt: new Date().toISOString(), proposalAt: createdAt, proposal: data.fit,
    fit: { familyId: data.familyId, confidence: data.confidence, flagged: data.flagged, runnersUp: data.runnersUp },
    rawResponsePath: data.rawResponsePath ?? null, usage: data.usage ?? null,
  };
  ui.checkEditing = null;
  ui.fieldErrors = {};
  ui.drafts = {};
  ui.bay = 0;
  const design = { ...cloneDesign(mapped.design), appearance: { ...app.store.design.appearance } };
  if (ui.empty || mode === 'new' || !projectHasValues()) {
    app.store.reset({ design, fields: mapped.fields, revisions: [], currentRevisionId: null, exports: [] });
  } else {
    app.store.apply(() => ({ design, fields: mapped.fields }), { label: 'New proposal', track: false });
  }
  ui.empty = false;
  const label = getFamily(data.familyId)?.definition.label ?? 'A shelf style';
  announce(`${label} proposed, ${Math.round(data.confidence * 100)} per cent sure. Ready for review.`);
  go('check');
  scheduleSave();
}

export function cancelAnalysis(): void {
  if (runId) void api.cancelIntake(runId);
}

/**
 * "Set up by hand from a style" (no-fit, ui-spec §2.2): the family chooser. Each registered
 * family starts a new project from its plainest preset, every field unresolved.
 */
export function chooseStyle(): void {
  choose({
    title: 'Set up by hand from a style',
    lede: 'Pick the shelf style closest to your piece. You start from its plainest version and check every value; nothing is taken from the photo.',
    choices: FAMILIES.map((f, i) => ({ label: f.definition.label, sub: f.definition.description, primary: i === 0, act: () => { void startManual(f.definition.id); } })),
  });
}

/**
 * "Set up by hand" always starts a new project: it never overwrites an accepted one. With a
 * family (the no-fit chooser, plan §3 step 2) it starts from that family's plainest preset.
 */
export async function startManual(familyId?: string): Promise<void> {
  try {
    await newProject({ from: 'preset:even', name: 'Untitled bookcase' });
    const fam = getFamily(familyId ?? 'grid-bookcase');
    const d = fam ? fam.create() : createDesign('even', app.store.stock);
    const design = { ...d, appearance: { ...d.appearance, name: 'Untitled bookcase' } };
    app.store.reset({ design, fields: fieldsForManualSetup(design, app.store.stock), revisions: [], currentRevisionId: null, exports: [] });
    ui.analysis = { status: 'manual', ranAt: new Date().toISOString() };
    ui.empty = false;
    go('check');
    toast('New project set up by hand from a plain even bookcase. Check each value.');
    scheduleSave();
  } catch (e) {
    toast(e instanceof ApiError ? e.message : 'A new project could not be created.', { tone: 'warn' });
  }
}
